<template>
  <div class="login-page">
    <div v-if="checkingAuth" class="login-container">
      <div class="login-box" style="text-align: center; padding: 60px 40px;">
        <img :src="require('@/assets/img/OVB_logo_gray.svg')" alt="OVB" class="login-logo" />
        <p style="color: #65676e; font-size: 14px; margin-top: 16px;">Loading...</p>
      </div>
    </div>
    <div v-else class="login-container">
      <div class="login-box">
      <div class="logo-section">
        <img :src="require('@/assets/img/OVB_logo_gray.svg')" alt="OVB" class="login-logo" />
        <h1 class="welcome-title">Welcome</h1>
        <p class="welcome-subtitle">Log in to OVB to continue</p>
      </div>

      <form @submit.prevent="handleLogin" class="login-form">
        <div class="input-wrapper">
          <label for="email" class="input-label">
            Email address
            <span class="required" aria-hidden="true">*</span>
          </label>
          <input
            id="email"
            v-model="email"
            type="email"
            class="input-field"
            inputmode="email"
            required
            autocomplete="email"
            autocapitalize="none"
            spellcheck="false"
            :disabled="loading"
            autofocus
          />
        </div>

        <div class="input-wrapper">
          <label for="password" class="input-label">
            Password
            <span class="required" aria-hidden="true">*</span>
          </label>
          <div class="password-field-wrapper">
            <input
              id="password"
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              class="input-field"
              required
              autocomplete="current-password"
              autocapitalize="none"
              spellcheck="false"
              :disabled="loading"
            />
            <button
              type="button"
              class="password-toggle"
              @click="showPassword = !showPassword"
              :aria-label="showPassword ? 'Hide password' : 'Show password'"
            >
              <span class="password-icon">{{ showPassword ? '👁️' : '👁️‍🗨️' }}</span>
            </button>
          </div>
        </div>

        <p class="forgot-password-link">
          <router-link to="/forgot-password" class="link">Forgot password?</router-link>
        </p>

        <div v-if="sessionExpiredMessage" class="info-message">
          {{ sessionExpiredMessage }}
        </div>

        <div v-if="error" class="error-message">
          {{ error }}
        </div>

        <button type="submit" :disabled="loading" class="continue-button">
          Continue
        </button>
      </form>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Login',
  data () {
    return {
      email: '',
      password: '',
      loading: false,
      error: null,
      showPassword: false,
      sessionExpiredMessage: null,
      checkingAuth: true
    }
  },
  mounted () {
    // Check if user was redirected due to session expiry
    if (this.$route.query.session === 'expired') {
      this.sessionExpiredMessage = 'Your previous session has expired, please log in again'
      this.$router.replace({ name: 'Login' })
      this.checkingAuth = false
      return
    }

    // If already logged in, redirect to home instead of showing login form
    const checkAuth = () => {
      if (this.$auth.isAuthenticated) {
        const homePath = this.$auth.user?.homePath || 'ClinicHome'
        // Safety: if redirect doesn't navigate away within 3s, show login form
        const safetyTimeout = setTimeout(() => { this.checkingAuth = false }, 3000)
        this.$router.push({ name: homePath }).catch(() => {
          this.$router.push('/').catch(() => {
            clearTimeout(safetyTimeout)
            this.checkingAuth = false
          })
        })
      } else {
        this.checkingAuth = false
      }
    }

    if (!this.$auth.loading) {
      checkAuth()
    } else {
      const timeout = setTimeout(() => {
        this.checkingAuth = false
      }, 2000)
      const unwatch = this.$watch(
        () => this.$auth.loading,
        (loading) => {
          if (!loading) {
            clearTimeout(timeout)
            checkAuth()
            unwatch()
          }
        }
      )
    }
  },
  methods: {
    async handleLogin () {
      this.loading = true
      this.error = null

      try {
        // Use the auth plugin (mimics Auth0 behavior)
        await this.$auth.loginWithCredentials(this.email, this.password)
        // loginWithCredentials handles redirect automatically
      } catch (err) {
        console.error('Login error:', err)
        this.error = this.$auth.error || 'Login failed. Please try again.'
      } finally {
        // Always re-enable the button. If login succeeded and redirect worked,
        // this component will be destroyed anyway. If redirect failed or login
        // errored, the user needs the button to try again.
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
/* Auth0-inspired design */
.login-page {
  min-height: 100vh;
  background: #000000;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
}

.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

.login-box {
  background: #ffffff;
  padding: 48px 40px;
  border-radius: 5px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.2);
  width: 100%;
  max-width: 450px;
}

.logo-section {
  text-align: center;
  margin-bottom: 32px;
}

.login-logo {
  width: auto;
  height: auto;
  max-height: 50px;
  max-width: 200px;
  margin-bottom: 24px;
}

.welcome-title {
  font-size: 20px;
  font-weight: 600;
  color: #000;
  margin: 0 0 16px 0;
  line-height: 1.3;
}

.welcome-subtitle {
  font-size: 14px;
  color: #65676e;
  margin: 0;
  line-height: 1.6;
}

.login-form {
  margin-top: 32px;
}

.input-wrapper {
  margin-bottom: 24px;
}

.input-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #000;
  margin-bottom: 8px;
  line-height: 1.5;
}

.required {
  color: #d00e17;
  margin-left: 2px;
}

.input-field {
  width: 100%;
  padding: 11px 12px;
  border: 1px solid #c9cccf;
  border-radius: 3px;
  font-size: 15px;
  line-height: 1.5;
  color: #000;
  background: #fff;
  box-sizing: border-box;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.input-field:hover {
  border-color: #b0b4b8;
}

.input-field:focus {
  outline: none;
  border-color: #b0b4b8;
  box-shadow: none;
}

.input-field:disabled {
  background: #f9f9f9;
  cursor: not-allowed;
  opacity: 0.6;
}

.password-field-wrapper {
  position: relative;
}

.password-toggle {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  font-size: 18px;
  line-height: 1;
  color: #65676e;
  transition: color 0.2s;
}

.password-toggle:hover {
  color: #000;
}

.password-icon {
  display: block;
}

.forgot-password-link {
  text-align: right;
  margin: -8px 0 24px 0;
}

.link {
  font-size: 14px;
  color: #000;
  text-decoration: none;
  font-weight: 600;
  transition: color 0.2s;
}

.link:hover {
  color: #000;
  text-decoration: underline;
}

.info-message {
  background: #e8f4f8;
  color: #0a5f7f;
  padding: 12px 16px;
  border-radius: 3px;
  margin-bottom: 16px;
  font-size: 14px;
  border: 1px solid #b3d9e8;
}

.error-message {
  background: #fee;
  color: #d00e17;
  padding: 12px 16px;
  border-radius: 3px;
  margin-bottom: 16px;
  font-size: 14px;
  border: 1px solid #fcc;
}

.continue-button {
  width: 100%;
  padding: 12px;
  background: #ef4356;
  color: #ffffff;
  border: none;
  border-radius: 3px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  line-height: 1.5;
}

.continue-button:hover:not(:disabled) {
  background: #d63848;
}

.continue-button:disabled {
  background: #f9b2ba;
  cursor: not-allowed;
}

</style>
